import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Heading } from '../components/ui/heading';
import { Timestamp } from '../components/ui/timestamp';
import { 
  DocumentTextIcon, 
  TrashIcon, 
  FunnelIcon,
  MagnifyingGlassIcon,
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { 
  getOmbudsmanDecisions, 
  deleteOmbudsmanDecisions,
  getOmbudsmanStatistics 
} from '../utils/api';
import { 
  OmbudsmanDecision, 
  OmbudsmanStatistics,
  OmbudsmanFilters,
  OmbudsmanDecisionListResponse 
} from '../types/ombudsman';
import OmbudsmanImportModal from '../components/ombudsman/OmbudsmanImportModal';
import OmbudsmanStatsDashboard from '../components/ombudsman/OmbudsmanStatsDashboard';
import ConfirmationModal from '../components/ConfirmationModal';

const OmbudsmanManagement: React.FC = () => {
  const [decisions, setDecisions] = useState<OmbudsmanDecision[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [selectedDecisions, setSelectedDecisions] = useState<Set<string>>(new Set());
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [statistics, setStatistics] = useState<OmbudsmanStatistics | null>(null);
  
  // Filters
  const [filters, setFilters] = useState<OmbudsmanFilters>({
    decision_status: '',
    industry_sector: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  const loadDecisions = useCallback(async () => {
    setLoading(true);
    try {
      const skip = (currentPage - 1) * pageSize;
      const filterParams = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== '')
      );
      
      const response: OmbudsmanDecisionListResponse = await getOmbudsmanDecisions(
        skip, 
        pageSize, 
        filterParams
      );
      
      setDecisions(response.decisions);
      setTotalCount(response.total_count);
    } catch (error) {
      console.error('Error loading ombudsman decisions:', error);
      toast.error('Failed to load ombudsman decisions');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filters]);

  const loadStatistics = useCallback(async () => {
    try {
      const stats = await getOmbudsmanStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  }, []);

  useEffect(() => {
    loadDecisions();
  }, [loadDecisions]);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  const handleFilterChange = (field: keyof OmbudsmanFilters, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleSelectDecision = (decisionId: string) => {
    setSelectedDecisions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(decisionId)) {
        newSet.delete(decisionId);
      } else {
        newSet.add(decisionId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedDecisions.size === decisions.length) {
      setSelectedDecisions(new Set());
    } else {
      setSelectedDecisions(new Set(decisions.map(d => d.id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedDecisions.size === 0) return;
    
    try {
      await deleteOmbudsmanDecisions(Array.from(selectedDecisions));
      toast.success(`Deleted ${selectedDecisions.size} decisions`);
      setSelectedDecisions(new Set());
      setShowDeleteModal(false);
      loadDecisions();
      loadStatistics();
    } catch (error) {
      console.error('Error deleting decisions:', error);
      toast.error('Failed to delete decisions');
    }
  };

  const handleDeleteAll = async () => {
    try {
      await deleteOmbudsmanDecisions();
      toast.success('All decisions deleted');
      setSelectedDecisions(new Set());
      setShowDeleteModal(false);
      loadDecisions();
      loadStatistics();
    } catch (error) {
      console.error('Error deleting all decisions:', error);
      toast.error('Failed to delete all decisions');
    }
  };

  const handleImportSuccess = () => {
    loadDecisions();
    loadStatistics();
    setShowImportModal(false);
  };

  const getStatusIcon = (status: string) => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'upheld':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'not upheld':
      case 'not_upheld':
      case 'notupheld':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'partially upheld':
      case 'partly upheld':
      case 'partly_upheld':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return <DocumentTextIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'upheld':
        return 'bg-red-100 text-red-800';
      case 'not upheld':
      case 'not_upheld':
      case 'notupheld':
        return 'bg-green-100 text-green-800';
      case 'partially upheld':
      case 'partly upheld':
      case 'partly_upheld':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <Heading level={1} weight="bold" className="text-gray-900">Ombudsman Decisions</Heading>
          <p className="text-gray-600">
            Manage and visualize ombudsman decisions for intelligent signal matching
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <FunnelIcon className="h-5 w-5" />
            Filters
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <CloudArrowUpIcon className="h-5 w-5" />
            Import Decisions
          </button>
        </div>
      </div>

      {/* Statistics Dashboard */}
      {statistics && (
        <OmbudsmanStatsDashboard statistics={statistics} />
      )}

      {/* Filters */}
      {showFilters && (
        <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search decisions..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Decision Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Decision Status
              </label>
              <select
                value={filters.decision_status}
                onChange={(e) => handleFilterChange('decision_status', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="Upheld">Upheld</option>
                <option value="Not Upheld">Not Upheld</option>
                <option value="Partially Upheld">Partially Upheld</option>
              </select>
            </div>

            {/* Industry Sector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Industry Sector
              </label>
              <select
                value={filters.industry_sector}
                onChange={(e) => handleFilterChange('industry_sector', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Sectors</option>
                <option value="Insurance">Insurance</option>
                <option value="Banking">Banking</option>
                <option value="Investment">Investment</option>
                <option value="Credit">Credit</option>
                <option value="Mortgage">Mortgage</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Actions Bar */}
      {selectedDecisions.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex justify-between items-center">
          <span className="text-blue-800">
            {selectedDecisions.size} decision(s) selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setShowDeleteModal(true)}
              className="flex items-center gap-2 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
            >
              <TrashIcon className="h-4 w-4" />
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Decisions Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedDecisions.size === decisions.length && decisions.length > 0}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reference
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Industry
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Business
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Decision Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Summary
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    Loading ombudsman decisions...
                  </td>
                </tr>
              ) : decisions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    No ombudsman decisions found. Import some decisions to get started.
                  </td>
                </tr>
              ) : (
                decisions.map((decision) => (
                  <tr key={decision.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedDecisions.has(decision.id)}
                        onChange={() => handleSelectDecision(decision.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-blue-600">
                        {decision.reference}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(decision.decision_status)}
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(decision.decision_status)}`}>
                          {decision.decision_status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {decision.industry_sector}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {decision.business_name || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <Timestamp value={decision.decision_date} format="date-only" />
                    </td>
                    <td className="px-6 py-4 max-w-md">
                      <div className="text-sm text-gray-900 truncate">
                        {decision.summary}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center text-sm text-gray-700">
                Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete All Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowDeleteModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          disabled={totalCount === 0}
        >
          <TrashIcon className="h-5 w-5" />
          Delete All Decisions
        </button>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <OmbudsmanImportModal
          onClose={() => setShowImportModal(false)}
          onSuccess={handleImportSuccess}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={selectedDecisions.size > 0 ? handleDeleteSelected : handleDeleteAll}
          title={selectedDecisions.size > 0 ? "Delete Selected Decisions" : "Delete All Decisions"}
          message={
            selectedDecisions.size > 0 
              ? `Are you sure you want to delete ${selectedDecisions.size} selected decisions? This action cannot be undone.`
              : "Are you sure you want to delete ALL ombudsman decisions? This action cannot be undone."
          }
          confirmText="Delete"
          isDestructive={true}
        />
      )}
    </div>
  );
};

export default OmbudsmanManagement;